import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import orderService from '../../db/order-service';
import pushNotificationManager from '../../services/push-notification-service';

const PaymentSuccessHandler = () => {
  const [processing, setProcessing] = useState(true);
  const [message, setMessage] = useState('Processing your payment...');
  const [emailStatus, setEmailStatus] = useState(null);
  const [isTrial, setIsTrial] = useState(false);
  const hasRun = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const handlePaymentSuccess = async () => {
      try {
        // Restore session from localStorage if sessionStorage was cleared by page reload
        if (!sessionStorage.getItem('customerId')) {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          if (user.id) {
            sessionStorage.setItem('isUserAuthenticated', 'true');
            sessionStorage.setItem('customerId', user.id);
            sessionStorage.setItem('customerEmail', user.email || '');
            sessionStorage.setItem('customerName', user.name || '');
            if (user.phone) sessionStorage.setItem('customerPhone', user.phone);
            sessionStorage.setItem('isAdmin', user.role === 'admin' ? 'true' : 'false');
          }
        }
        // Get query parameters from URL
        const queryParams = new URLSearchParams(location.search);
        const orderId = queryParams.get('orderId');
        const paymentId = queryParams.get('paymentId');
        const subscriptionType = queryParams.get('subscriptionType');
        const planType = queryParams.get('planType');
        
        // Handle subscription payments (including free trial)
        if (subscriptionType === 'subscription' || planType === 'free_trial') {
          await handleSubscriptionPayment(paymentId, planType);
          return;
        }
        
        // Handle regular order payments
        if (!orderId) {
          setMessage('Error: Missing order information');
          setProcessing(false);
          return;
        }
        
        await handleOrderPayment(orderId, paymentId);
        
      } catch (error) {
        console.error('Error processing payment:', error);
        setMessage('Payment processing error. Please contact customer support.');
        setProcessing(false);
      }
    };
    
    handlePaymentSuccess();
  }, [location.search, navigate]);

  const handleSubscriptionPayment = async (paymentId, planType) => {
    try {
      if (planType === 'free_trial') {
        setIsTrial(true);
        setMessage('🎁 Processing your free trial activation...');

        const customerId = sessionStorage.getItem('customerId');
        const customerEmail = sessionStorage.getItem('customerEmail');
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const customerPhone = storedUser.phone || sessionStorage.getItem('customerPhone') || '';

        if (!customerId || !customerEmail) {
          throw new Error('Session expired. Please log in again.');
        }

        // Read delivery schedule stored by SubscriptionCheckout before Razorpay redirect
        // localStorage is used because sessionStorage is wiped on cross-origin redirect
        // Validate customerId to prevent stale schedule from a different user on same device
        const storedScheduleData = JSON.parse(localStorage.getItem('trialDeliverySchedule') || '{}');
        const deliverySchedule = (storedScheduleData.customerId === customerId)
          ? storedScheduleData.schedule
          : {};

        // Derive selectedProducts from the delivery schedule
        const productCounts = {};
        Object.values(deliverySchedule).forEach(day => {
          if (day.shot) productCounts[day.shot] = (productCounts[day.shot] || 0) + 1;
        });
        const selectedProducts = Object.entries(productCounts)
          .map(([productId, quantity]) => ({ productId, quantity }));

        const freeTrialService = (await import('../../services/free-trial-service')).default;

        // This is the critical call — creates subscription doc and sets freeTrialClaimed flag
        const result = await freeTrialService.claimTrial(
          customerId,
          customerEmail,
          customerPhone,
          paymentId,
          selectedProducts,
          deliverySchedule
        );

        // Clear handoff data
        localStorage.removeItem('trialDeliverySchedule');
        // trialJustClaimed flag stays — it's the fast-path guard in SubscriptionPlans

        if (result && result.error) {
          setMessage(`Note: ${result.error}. If you already have a trial, check "My Subscriptions".`);
        } else {
          setMessage('🎁 Welcome Gift activated! Your 5-day delivery schedule is ready.');
        }

        window.dispatchEvent(new CustomEvent('refreshSubscriptions'));
        setEmailStatus({ success: true, message: '🏪 Store Pickup Only — Opp Candeur, Bapu Nagar, Serilingampalle (M), Hyderabad – 500019. Pickup time: 8:00 AM – 9:00 AM (Mon–Fri). Contact: +91 9390649371' });
        // No auto-redirect — user clicks the button to go see their schedule

      } else {
        // Regular subscription payment
        setMessage('Activating your subscription...');

        try {
          const subscriptionService = (await import('../../services/subscription-service')).default;
          const pending = JSON.parse(localStorage.getItem('pendingSubscription') || '{}');

          if (!pending.customerId || !pending.planType) {
            throw new Error('Subscription data missing. Please contact support.');
          }

          const subscription = await subscriptionService.createSubscription(
            pending.customerId,
            pending.planType,
            pending.selectedProducts,
            pending.selectedAddress,
            pending.deliverySchedule
          );

          // Apply credits if any
          if (pending.appliedCredits > 0) {
            try {
              const bottleReturnService = (await import('../../db/bottle-return-service')).default;
              await bottleReturnService.useCredits(pending.customerId, pending.appliedCredits, subscription._id);
            } catch (creditsError) {
              console.error('Error applying credits:', creditsError);
            }
          }

          localStorage.removeItem('pendingSubscription');
          setMessage('🎉 Subscription activated successfully!');
          window.dispatchEvent(new CustomEvent('refreshSubscriptions'));
          setEmailStatus({ success: true, message: 'Your subscription is now active! Check "My Subscriptions" to view your schedule.' });

          setTimeout(() => {
            navigate('/customer');
            setTimeout(() => window.dispatchEvent(new CustomEvent('setCustomerTab', { detail: 'subscriptions' })), 100);
          }, 3000);

        } catch (subError) {
          console.error('Error creating subscription after payment:', subError);
          setMessage('Payment received but subscription activation failed. Please contact support.');
        }
      }
    } catch (error) {
      console.error('Error handling subscription payment:', error);
      setMessage(`Payment received but activation failed: ${error.message}. Please contact support.`);
    } finally {
      setProcessing(false);
    }
  };

  const handleOrderPayment = async (orderId, paymentId) => {
    try {
      const result = await orderService.updateOrderStatus(orderId, 'paid', paymentId);
      
      if (result.success) {
        setMessage('Payment successful! Your order has been confirmed.');
        
        if (pushNotificationManager.permission === 'granted') {
          pushNotificationManager.notifyOrderPlaced(orderId);
        }
        
        if (result.order && result.order.emailSent) {
          if (result.order.emailSent.success) {
            setEmailStatus({
              success: true,
              message: `Confirmation email sent to ${result.order.customerEmail || 'your email address'}`
            });
          } else {
            setEmailStatus({
              success: false,
              message: 'Could not send confirmation email. Please check your email settings.'
            });
          }
        } else {
          setEmailStatus({
            success: false,
            message: 'No confirmation email was sent. Please contact customer support.'
          });
        }
        
        setTimeout(() => {
          navigate('/orders');
        }, 5000);
      } else {
        setMessage('There was an issue updating your order. Please contact customer support.');
      }
    } catch (error) {
      console.error('Error handling order payment:', error);
      setMessage('Payment processing error. Please contact customer support.');
    } finally {
      setProcessing(false);
    }
  };
  
  return (
    <div className="payment-success-container">
      <div className="payment-status-card">
        {processing ? (
          <div className="processing">
            <div className="spinner"></div>
            <p>{message}</p>
          </div>
        ) : (
          <div className="result">
            <div className={message.includes('Error') ? 'error-icon' : 'success-icon'}>
              {message.includes('Error') ? '✗' : '✓'}
            </div>
            <h2>{message.includes('Error') ? 'Payment Issue' : 'Payment Successful'}</h2>
            <p>{message}</p>
            
            {emailStatus && (
              <div className={`email-status ${emailStatus.success ? 'email-success' : 'email-error'}`}>
                <p><strong>Email Status:</strong> {emailStatus.message}</p>
                {!emailStatus.success && (
                  <p className="email-help">
                    If you didn't receive an email, please check your spam folder or contact support.
                  </p>
                )}
              </div>
            )}

            {isTrial ? (
              <button onClick={() => {
                navigate('/customer');
                setTimeout(() => window.dispatchEvent(new CustomEvent('setCustomerTab', { detail: 'subscriptions' })), 200);
              }}>
                📅 View My Delivery Schedule
              </button>
            ) : (
              <button onClick={() => {
                navigate('/customer');
                setTimeout(() => window.dispatchEvent(new CustomEvent('setCustomerTab', { detail: 'subscriptions' })), 200);
              }}>
                📊 View My Subscription
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* EmailDebugger removed */}
    </div>
  );
};

export default PaymentSuccessHandler;